import { useSwipe } from '#hooks/useSwipe';
import c from 'classnames';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button';
import { Icon } from '../Icon';
import styles from './CarouselModal.module.css';
import { type SlideDirection, SlideTransition } from './SlideTransition';

export type CarouselModalSlide = React.ReactNode | React.ComponentType;

type CarouselModalProps = {
  visible: boolean;
  slides: CarouselModalSlide[];
  background?: React.ReactNode;
  enableSwipe?: boolean;
  disableAnimation?: boolean;
  containerClassName?: string;
  modalClassName?: string;
  modalContentClassName?: string;
  onClose?: () => void; // omitted = not closable
};

const renderSlide = (S: React.ComponentType) => <S />;

export function CarouselModal({
  visible,
  onClose,
  slides,
  background,
  enableSwipe,
  disableAnimation,
  ...cn
}: CarouselModalProps) {
  const [slideNumber, setSlideNumber] = useState(0);
  const [direction, setDirection] = useState<SlideDirection>(1);

  const swipe = useSwipe({
    onSwipeLeft: () => handleStep('next'),
    onSwipeRight: () => handleStep('previous'),
  });

  if (!visible || slides.length === 0 || typeof document === 'undefined')
    return null;

  const slide = slides[slideNumber];
  const content = typeof slide === 'function' ? renderSlide(slide) : slide;

  function handleStep(action: 'previous' | 'next') {
    setDirection(action === 'next' ? 1 : -1);
    setSlideNumber(prev => {
      const next = action === 'next' ? prev + 1 : prev - 1;

      return Math.min(Math.max(next, 0), slides.length - 1);
    });
  }

  return createPortal(
    <div className={c(styles.container, cn.containerClassName)}>
      <div
        className={c(styles.modal, cn.modalClassName)}
        {...(enableSwipe ? swipe : {})}
      >
        {background}
        {onClose ? (
          <Button
            onClick={onClose}
            variant='wrapper'
            className={styles.closeButton}
          >
            <Icon name='closeSm' />
          </Button>
        ) : null}
        <SlideTransition
          slideKey={slideNumber}
          direction={direction}
          disabled={disableAnimation}
          className={cn.modalContentClassName}
        >
          {content}
        </SlideTransition>
        {slideNumber > 0 && (
          <Button
            variant='wrapper'
            onClick={() => handleStep('previous')}
            className={c(styles.navigationButton, styles.previous)}
          >
            <Icon name='arrowBack' color='purple-300' />
          </Button>
        )}
        <div className={styles.stepCircles}>
          {[...new Array(slides.length)].map((_, idx) => (
            <span
              key={idx}
              className={c(
                styles.stepCircle,
                idx === slideNumber && styles.active,
              )}
            />
          ))}
        </div>
        {slideNumber < slides.length - 1 && (
          <Button
            variant='wrapper'
            onClick={() => handleStep('next')}
            className={c(styles.navigationButton, styles.next)}
          >
            <Icon name='arrowForward' color='purple-300' />
          </Button>
        )}
      </div>
      <div className={styles.backdrop} />
    </div>,
    document.body,
  );
}
